'use client';

import { motion, useInView, useAnimation, Variant } from 'framer-motion';
import { JSX, useEffect, useRef } from 'react';

type AnimatedHeadingsProps = {
  text: string | string[];
  el?: keyof JSX.IntrinsicElements;
  className?: string;
  once?: boolean;
  repeatDelay?: number;
  animation?: {
    hidden: Variant;
    visible: Variant;
  };
};

const defaultAnimations: AnimatedHeadingsProps['animation'] = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.1,
    },
  },
};

const AnimatedHeadings = ({
  text,
  el: Wrapper = 'p',
  className,
  once = true,
  repeatDelay = 0,
  animation = defaultAnimations,
}: AnimatedHeadingsProps) => {
  const controls = useAnimation();
  const textArray = Array.isArray(text) ? text : [text];
  const ref = useRef(null);
  const isInView = useInView(ref, { amount: 0.5, once });

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    const triggerAnimation = async () => {
      if (isInView) {
        await controls.start('visible');
        if (repeatDelay) {
          timeoutId = setTimeout(async () => {
            await controls.start('hidden');
            await triggerAnimation();
          }, repeatDelay);
        }
      } else {
        controls.start('hidden');
      }
    };

    triggerAnimation();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isInView, controls, repeatDelay]);

  return (
    <Wrapper className={className}>
      <span className="sr-only">{textArray.join(' ')}</span>
      <motion.span
        ref={ref}
        initial="hidden"
        animate={controls}
        variants={{
          visible: { transition: { staggerChildren: 0.1 } },
          hidden: {},
        }}
        aria-hidden
      >
        {textArray.map((line, lineIndex) => (
          <span className="block" key={`${line}-${lineIndex}`}>
            {line.split(' ').map((word, wordIndex) => (
              <span className="inline-block" key={`${word}-${wordIndex}`}>
                {word.split('').map((char, charIndex) => (
                  <motion.span
                    key={`${char}-${charIndex}`}
                    className="inline-block"
                    variants={animation}
                  >
                    {char}
                  </motion.span>
                ))}
                <span className="inline-block">&nbsp;</span>
              </span>
            ))}
          </span>
        ))}
      </motion.span>
    </Wrapper>
  );
};

export default AnimatedHeadings;
